"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, MapPin, BrainCircuit, GraduationCap, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "01",
  title: "Application",
  description: "Complete our detailed puppy application to help us understand your home environment and ensure a perfect lifestyle match for our Cavaliers.",
  icon: MessageSquare
}, {
  id: "02",
  title: "Consultation",
  description: "A personal conversation to discuss our breeding philosophy, health testing standards, and the specific needs of your future companion and the environment you and your Cavalier will live in.",
  icon: MapPin
}, {
  id: "03",
  title: "Selection & Commitment",
  description: "Secure your place on our exclusive waitlist and meet your puppy as their unique personalities and temperaments begin to shine.",
  icon: BrainCircuit
}, {
  id: "04",
  title: "Homecoming",
  description: "The moment you've waited for. Welcome your health-certified, well-socialized puppy into your forever family with our full support.",
  icon: GraduationCap
}];
export default function PathToEnrollment() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const triggerPoint = windowHeight * 0.6;
      const scrolled = triggerPoint - rect.top;
      const totalHeight = rect.height;
      let progress = scrolled / totalHeight * 100;
      progress = Math.max(0, Math.min(100, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="129656" id="adoption-process" className="py-24 md:py-32 bg-background relative overflow-hidden bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1536339/img/family_with_cavalier_puppy.png)]">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-sans tracking-widest text-secondary uppercase mb-4 font-semibold">Our Journey Together</h2>
          <h3 className="text-4xl md:text-5xl font-sans font-bold text-foreground mb-6 tracking-tight">
            The Path to Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/80">New Companion</span>
          </h3>
          <p className="text-lg text-foreground/70 font-normal">
            Adopting a Mile High Cavalier is a thoughtful process designed to ensure a lifetime of joy for both you and your new puppy.
          </p>
        </div>

        <div className="relative" ref={containerRef}>
          {}
          <div className="absolute left-8 md:left-24 top-4 bottom-4 w-0.5 border-l-2 border-dotted border-secondary/30" />
          
          {}
          <div className="absolute left-8 md:left-24 top-4 w-0.5 bg-secondary transition-all duration-300 ease-out shadow-[0_0_12px_rgba(100,120,80,0.3)]" style={{
          height: `calc(${scrollProgress}% - 2rem)`
        }} />

          <div className="space-y-16 md:space-y-24">
            {steps.map((step, i) => {
            const activationThreshold = i === 0 ? 0 : i / (steps.length - 1) * 100 - 15;
            const isActive = scrollProgress >= activationThreshold;
            return <div key={step.id} data-index={i} className="relative flex items-start group">
                  
                  {}
                  <div className="hidden md:flex absolute left-0 w-16 justify-end pt-3 pr-8">
                    <span className={`text-2xl font-sans font-bold transition-colors duration-500 ${isActive ? "text-primary" : "text-foreground/20"}`}>
                      {step.id}
                    </span>
                  </div>

                  {}
                  <div className="absolute left-8 md:left-24 -translate-x-1/2 pt-3">
                    <div className={`w-8 h-8 rounded-full border-4 flex items-center justify-center transition-all duration-500 z-10 bg-background ${isActive ? "border-secondary shadow-[0_0_20px_rgba(100,120,80,0.2)] scale-110" : "border-border scale-100"}`}>
                      <div className={`w-2 h-2 rounded-full transition-colors duration-500 ${isActive ? "bg-secondary" : "bg-transparent"}`} />
                    </div>
                  </div>

                  {}
                  <div className="ml-20 md:ml-40 w-full max-w-2xl">
                    <Card className={`transition-all duration-700 border-border/50 rounded-none shadow-sm hover:shadow-md ${isActive ? "opacity-100 translate-x-0" : "opacity-40 translate-x-8"}`}>
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-4 mb-2">
                          <div className={`p-2.5 rounded-none transition-colors duration-500 ${isActive ? "bg-primary/10 text-primary" : "bg-muted text-foreground/40"}`}>
                            <step.icon className="w-6 h-6" />
                          </div>
                          <CardTitle className="text-2xl font-sans font-semibold text-foreground">{step.title}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base leading-relaxed text-foreground/70 font-normal">
                          {step.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </div>

                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 text-center relative z-10">
          <div className="inline-flex flex-col items-center">
            <p className="text-foreground/60 font-sans mb-6 tracking-wide uppercase text-xs font-bold">Ready to take the first step?</p>
            <Link to="/ApplicationPage">
              <Button size="lg" className="rounded-none px-10 h-14 text-base shadow-xl shadow-primary/10 hover:shadow-primary/20 bg-primary text-primary-foreground transition-all group">
                Begin Your Application
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>;
}